import { BehaviorSubject, Observable, takeUntil } from 'rxjs';

import { EventEmitter, inject, Injectable, OnDestroy } from '@angular/core';
import { FirebaseError } from '@angular/fire/app';
import { Auth, authState, signOut, updateProfile, User } from '@angular/fire/auth';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { OrganisationSelectedService } from '@organisations/services/organisation-selected/organisation-selected.service';
import { AnalyticEvent } from '@shared/models/analytics';
import { AnalyticsService } from '@shared/services/analytics/analytics.service';

import { AuthToastService } from '../auth-toast/auth-toast.service';

@Injectable({
	providedIn: 'root',
})
export class AuthService implements OnDestroy {
	private analyticsService = inject(AnalyticsService);
	private auth = inject(Auth);
	private authToastService = inject(AuthToastService);
	private organisationSelectedService = inject(OrganisationSelectedService);

	private destroyed$ = new EventEmitter<void>();
	private currentUserSubject = new BehaviorSubject<User | null>(null);
	currentUser$: Observable<User | null> = authState(this.auth);

	constructor() {
		this.currentUser$.pipe(takeUntil(this.destroyed$)).subscribe((user) => {
			this.analyticsService.setUserId(user ? user.uid : null);
			this.currentUserSubject.next(user);
		});
	}

	ngOnDestroy(): void {
		this.destroyed$.emit();
	}

	get currentUser(): User | null {
		return this.auth.currentUser;
	}

	async signOut(): Promise<void> {
		this.organisationSelectedService.clearSelectedOrganisation();
		this.analyticsService.logEvent(AnalyticEvent.SIGN_OUT);

		await FirebaseAuthentication.signOut();
		await signOut(this.auth);
	}

	async updateUserProfile(newDisplayName: string, photoURL?: string): Promise<void> {
		try {
			await updateProfile(this.currentUser!, { displayName: newDisplayName, photoURL: photoURL });
			this.analyticsService.logEvent(AnalyticEvent.PROFILE_UPDATE);
		} catch (error: unknown) {
			console.error(error);
			if (error instanceof FirebaseError) {
				this.authToastService.handleError('Profile Update Error', error.code);
			}
		}
	}
}
